import { useCallback, useEffect, useMemo, useState } from 'react'
import Loading from 'components/Loading/Loading'
import { Alert, Box, Container, Grid, Typography } from '@mui/material'
import { isNil, sortBy } from 'lodash-es'
import { AdvertisementType, CustomerDisplaySettingContract } from 'models'
import Carousel from 'components/Carousel/Carousel'
import { useCFDContext } from 'pages/CustomerFacingDisplayPage/context/useCFDContext'
import usePOSRequestCaller from 'pages/CustomerFacingDisplayPage/hook/usePosRequestCaller'

function CFDAdvertising() {
    const { clientID, isConnected } = useCFDContext()
    const [
        getCustomerDisplaySettingLoading,
        setGetCustomerDisplaySettingLoading,
    ] = useState(false)

    const [customerDisplaySetting, setCustomerDisplaySetting] = useState<
        CustomerDisplaySettingContract | undefined
    >(undefined)
    const { get } = usePOSRequestCaller()
    const getCustomerDisplaySetting = useCallback(async () => {
        try {
            setGetCustomerDisplaySettingLoading(true)
            const res = await get<CustomerDisplaySettingContract>(
                `/location/customer-display-settings/show/${clientID}`,
            )

            setCustomerDisplaySetting(res?.data)
        } catch (error) {
        } finally {
            setGetCustomerDisplaySettingLoading(false)
        }
    }, [clientID, get])

    const adsImages = useMemo(() => {
        return sortBy(
            customerDisplaySetting?.customerDisplaySettingAdvertiseImages,
            (i) => i.sortNumber,
        ).map((i) => i?.advertiseImageImageURL ?? '')
    }, [customerDisplaySetting?.customerDisplaySettingAdvertiseImages])

    useEffect(() => {
        if (clientID) getCustomerDisplaySetting()
    }, [clientID, getCustomerDisplaySetting])

    if (getCustomerDisplaySettingLoading) return <Loading />
    return (
        <Container sx={(x) => ({ background: x.palette.background.paper })}>
            <Box
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'flex-start'}
                sx={{ height: '97svh' }}
            >
                {customerDisplaySetting ? (
                    <Grid container>
                        <Grid item container alignItems="center" margin={2}>
                            {customerDisplaySetting?.showLocationLogo &&
                                customerDisplaySetting?.locationLogoURL && (
                                    <Grid item>
                                        <img
                                            src={
                                                customerDisplaySetting?.locationLogoURL ??
                                                ''
                                            }
                                            alt="location logo"
                                            style={{
                                                height: 100,
                                            }}
                                        />
                                    </Grid>
                                )}
                            <Box flexGrow={1} />
                            {customerDisplaySetting?.companyLogoURL && (
                                <Grid item>
                                    <img
                                        src={
                                            customerDisplaySetting?.companyLogoURL
                                        }
                                        alt="company logo"
                                        style={{
                                            height: 100,
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        <Grid
                            item
                            container
                            justifyContent="center"
                            padding={4}
                        >
                            {!isNil(customerDisplaySetting?.welcomeMessage) && (
                                <Grid
                                    item
                                    container
                                    justifyContent="center"
                                    padding={2}
                                >
                                    <Typography variant="h4" color="primary">
                                        {customerDisplaySetting?.welcomeMessage ??
                                            ''}
                                    </Typography>
                                </Grid>
                            )}
                            {customerDisplaySetting?.advertisementType ===
                                AdvertisementType.Image && (
                                <Carousel images={adsImages ?? []} />
                            )}

                            {customerDisplaySetting?.advertisementType ===
                                AdvertisementType.Video && (
                                <video
                                    style={{
                                        width: '100%',
                                        overflow: 'hidden',
                                    }}
                                    src={
                                        customerDisplaySetting.promotionVideoURL ??
                                        ''
                                    }
                                    autoPlay
                                    loop
                                    muted
                                />
                            )}
                        </Grid>
                    </Grid>
                ) : (
                    <Box height={'100%'} alignContent={'center'}>
                        <Alert severity="error">
                            {isConnected
                                ? "Unable to get the Customer facing display setting. Please verify that the client's setting correctly."
                                : 'Unable to connect the POS Terminal. Please verify that the connection on client is available'}
                        </Alert>
                    </Box>
                )}
            </Box>
        </Container>
    )
}
export default CFDAdvertising
